/* eslint-disable @if6/fi-no-absolute-urls */
import { Box, Skeleton, Stack, ThemeProvider } from '@mui/material';
import { Grid } from '@portals/core/src/components/Grid/Grid';
import { createTheme as createDarkTheme } from '@portals/core/src/themes/sde/dark';
import { createTheme as createFooterTheme } from '@portals/core/src/themes/sde/footer';
import { createTheme as createHeaderTheme } from '@portals/core/src/themes/sde/header';
import { createTheme as createLightTheme } from '@portals/core/src/themes/sde/light';
import { theme } from '@portals/core/src/themes/sde/main';
import { ConfigurationContext as FifiConfigurationContext } from '@portals/fifi/src/contexts/configuration';
import {
  ConfigurationContext as JobsConfigurationContext,
  JOBS_APP_CONFIG,
} from '@portals/jobs/src/contexts/configuration';
import Head from 'next/head';
import Image from 'next/image';
import React, { ErrorInfo } from 'react';
import { JsonLd } from 'react-schemaorg';
import { Article } from 'schema-dts';

import {
  ContentButton,
  ContentOpener,
  InternArticleMeta,
  InternHeadline,
  InternHeadlineRichtext,
  InternText,
  isAdditionalCostCalculator,
  isAdviceBox,
  isAppButton,
  isAppCategoryLinkList,
  isAppFeaturePreview,
  isAppReview,
  isAuthor,
  isBenefitsList,
  isBenefitsListImage,
  isBruttoNettoCalculator,
  isCardContentCard,
  isCardInfoCard,
  isCardMetaCard,
  isCardProductCard,
  isCardServiceCard,
  isCategoryLinkList,
  isContentABTestingContent,
  isContentAccordionDownload,
  isContentAdviceBox,
  isContentAppButton,
  isContentAppCategoryLinkList,
  isContentAppFeaturePreview,
  isContentAppReview,
  isContentArticleText,
  isContentAudio,
  isContentAuthor,
  isContentAvatar,
  isContentButton,
  isContentCard,
  isContentCategoryLinkList,
  isContentCiveySurvey,
  isContentComparisonTable,
  isContentCooperationInfobox,
  isContentCTASection,
  isContentDatawrapperCharts,
  isContentFAQ,
  isContentFinder,
  isContentFooterBar,
  isContentGenericComparison,
  isContentHighCharts,
  isContentIconLinkList,
  isContentIfClickPath,
  isContentImagesDecorative,
  isContentImagesInformative,
  isContentImageWall,
  isContentInfo,
  isContentInPageNavigation,
  isContentInPageNavigationOpener,
  isContentInstituteSpecificContent,
  isContentIntro,
  isContentJobSearch,
  isContentLinda,
  isContentMenuLinkList,
  isContentNumberCounter,
  isContentNumberDivider,
  isContentOpener,
  isContentOpenerContentPage,
  isContentOpenerLandingPage,
  isContentPersonalizedContent,
  isContentPortraitBox,
  isContentProductLinkList,
  isContentQuiz,
  isContentQuote,
  isContentSCert,
  isContentSeaExitBox,
  isContentServiceLinkList,
  isContentServiceTeaserGallery,
  isContentSimpleTable,
  isContentSiteSearch,
  isContentSiteSearchSuggest,
  isContentStepper,
  isContentTopInfoBox,
  isContentTrustElement,
  isContentTrustImages,
  isContentVideo,
  isContentWallTeaserGallery,
  isCooperationInfobox,
  isDiscountCalculator,
  isFAQ,
  isFinder,
  isFlexFooter,
  isFooter,
  isFormsBudgetCalculator,
  isFormsCapitalCalculator,
  isFormsContactForm,
  isFormsFuelCostCalculator,
  isFormsHomeConstructionLoanCalculator,
  isFormsModernisationCalculator,
  isFormsPensionCalculator,
  isFormsPensionGapCalculator,
  isFormsPriceEstimator,
  isFormsPrivateLoanCalculator,
  isFormsRentalCalculator,
  isFormsRepaymentCalculator,
  isFormsSkpCalculator,
  isFormsSparkasseAssignmentBox,
  isFormsSparkasseAssignmentForm,
  isFullPageContentOpener,
  isGenericComparison,
  isGenericComparisonEntry,
  isGirokontoBenefits,
  isGirokontoComparison,
  isGirokontoConditions,
  isGirokontoServices,
  isHeader,
  isHeaderBar,
  isHeaderButtonList,
  isHeaderNavigation,
  isHouseholdCalculator,
  isIbanCalculator,
  isIfClickPath,
  isInfoCard,
  isInternArticleMeta,
  isInternContentItem,
  isInternGenericComparisonPriced,
  isInternGenericComparisonPriceFree,
  isInternHeadline,
  isInternHeadlineRichtext,
  isInternIcon,
  isInternInstituteSpecificContentItem,
  isInternMeta,
  isInternServiceLink,
  isInternSocialMeta,
  isInternText,
  isInternUserContext,
  isLegalFooter,
  isLinkIndexList,
  isMetaCard,
  isNavigation,
  isPage,
  isPageLinkList,
  isPortraitBox,
  isProductCard,
  isProductComparison,
  isProductLinkList,
  isQuickNavi,
  isQuiz,
  isSaveplanCalculator,
  isServiceCard,
  isServiceLinkList,
  isServiceTeaserGallery,
  isSplitPaper,
  isTrustElement,
  isUserContext,
  isWallTeaserGallery,
  Page,
} from '../../../models';
import { SKIP_TO_MAIN_CONTENT_ANCHOR } from '../../components/HeaderBar/HeaderBar.constants';
import { RequestHeadersContext, useRequestHeaders } from '../../contexts/request-headers';
import { SiteSearchContextProvider } from '../../contexts/site-search';
import { useLinkProps } from '../../hooks/useLinkProps';
import getAbsoluteUrlForMedia from '../../utils/getAbsoluteUrlForMedia';
import { loadIcon } from '../../utils/loadIcon';
import { CardConsumer } from '../CardConsumer/CardConsumer.dynamicImport';
import {
  AccordionDownload,
  AdditionalCostCalculator,
  AdviceBox,
  AppButton,
  AppCategoryLinkList,
  AppFeaturePreview,
  AppReview,
  Audio,
  Avatar,
  BenefitsList,
  BenefitsListImage,
  Breadcrumbs,
  BruttoNettoCalculator,
  BudgetCalculator,
  Button,
  Canonical,
  CapitalCalculator,
  CategoryLinkList,
  CiveySurvey,
  ComparisonTable,
  ContactForm,
  ContentIntro,
  CooperationInfobox,
  CTASection,
  DatawrapperCharts,
  DiscountCalculator,
  Divider,
  DividerLink,
  FAQ,
  FinalContainer,
  FinalGridContainer,
  FinalGridItem,
  Finder,
  FlexFooter,
  Footer,
  FuelCostCalculator,
  GenericComparison,
  GenericComparisonEntry,
  GenericComparisonPriceFree,
  GenericComparisonPricePriced,
  GirokontoBenefits,
  GirokontoComparison,
  GirokontoConditions,
  GirokontoServices,
  Header,
  HeaderBar,
  HeaderButtonList,
  HighCharts,
  HomeConstructionLoanCalculator,
  HouseholdCalculator,
  IbanCalculator,
  IconButton,
  IconList,
  IfClickPath,
  ImagesDecorative,
  ImagesInformative,
  ImageWall,
  Info,
  InPageNavigationOpener,
  InPageNavigationPage,
  InstituteSpecificContent,
  JobSearch,
  LegalFooter,
  Linda,
  Link,
  MenuLink,
  MenuLinkList,
  ModernisationCalculator,
  Navigation,
  NumberCounter,
  Opener,
  OpenerActions,
  OpenerBody,
  OpenerContent,
  OpenerContentPage,
  OpenerContentPageActions,
  OpenerContentPageContent,
  OpenerContentPageMedia,
  OpenerFoot,
  OpenerHead,
  OpenerLandingPage,
  OpenerMedia,
  OpenerVideo,
  PageLinkList,
  PensionCalculator,
  PensionGapCalculator,
  PortalsTopInfoBox,
  PortraitBox,
  Preview,
  PriceEstimator,
  PrivateLoanCalculator,
  ProductComparison,
  ProductLinkList,
  QuickNavi,
  Quiz,
  Quote,
  ReactPlayer,
  RentalCalculator,
  RepaymentCalculator,
  RichTextRenderer,
  SaveplanCalculator,
  SCert,
  SeaExitBox,
  ServiceBoxDialog,
  ServiceLink,
  ServiceLinkList,
  ServiceTeaserGallery,
  SimpleTable,
  SiteSearch,
  SiteSearchMeta,
  SiteSearchSuggest,
  SkpCalculator,
  Spacer,
  SparkasseAssignmentBox,
  SparkasseAssignmentForm,
  SparkasseAssignmentFormModal,
  SplitPaper,
  Stepper,
  TopInfoBox,
  TopInfoBoxItemText,
  TrustElement,
  TrustImages,
  Typography,
  UserContext,
  Video,
  WallTeaserGallery,
} from '../dynamicImports';
import { TextConsumer } from '../TextConsumer/TextConsumer.dynamicImport';

const FILIALFINDER_API_ENDPOINT_URL = process.env.FILIALFINDER_API_ENDPOINT_URL;

export const fifiConfig = {
  bankCode: '90090090',
  serviceUrl: `${FILIALFINDER_API_ENDPOINT_URL}/filialfinderapi/`,
  avatarUrl: `${FILIALFINDER_API_ENDPOINT_URL}/filialfinderapi`,

  useHashRouter: undefined,
  searchRoute: '/karte/:latitude/:longitude/:query([^/]*)?',
  homeRoute: '/',
  atmRoute: '/geldautomaten/:slug-:objectId(\\d+)',
  branchRoute: '/filialen/:slug-:objectId(\\d+)',
  contactsSearchRoute: '/beratersuche/:query([^/]*)?',

  yellowmapTileUrl: 'https://{s}.yellowmaps.eu/{t}/{z}/{x}/{y}{r}.png',
  yellowmapAutocompleteUrl: 'https://autocomplete.smartmaps.cloud/api/v5/Autocomplete',
  yellowmapSystemPartner: 'SparkasseFilialfinder',
  yellowmapApiKey: 'ipd7sJFv5qyN8cNzh8vdE6RFQ4TYh+Rb67xFLWZgTu8=',
  yellowmapFrontendKey: 'q+8lkqJayifNpCORagJY5otk9g5396xEm08brxMPdD/zLzC0JPHM7HPPi3bh0Obn',
  yellowmapApiUrl: 'https://www.yellowmap.de',
  yellowmapApiSessionUrl: 'https://www.yellowmap.de/api_rst/api/mapsession?provider=YELLOWMAPS',

  vcardLink: `${FILIALFINDER_API_ENDPOINT_URL}/filialfinderapi/vcard.php`,
  navigationLink: 'https://maps.google.com/maps',

  baseUrl: '',
  excludeBlz: '',
};

export type SingleNode = {
  id?: string | number;
  __contentType?: string;
  __component?: string;
  __eager?: { state: 'partial' | 'loaded' | 'failed' };
};
export type Node = SingleNode | Array<SingleNode>;

interface Props {
  node: Node;
}

interface State {
  error: boolean;
  errorName: string;
  errorMessage: string;

  node: Node;
}

const ErrorNotice = ({ headline, message }: { headline: string; message: string }) => {
  return (
    <div
      style={{
        border: '1px dotted silver',
        borderTop: '3px solid red',
        padding: '32px',
        boxShadow: 'rgba(0, 0, 0, 0.25) 0px 8px 32px 0px',
      }}
    >
      <h1>{headline || 'Error'}</h1>
      <p>{message || 'Error'}</p>
    </div>
  );
};

interface RenderInternArticleMetaProps {
  node: InternArticleMeta;
}

const RenderJsonLD: React.FC<RenderInternArticleMetaProps> = ({ node }) => {
  const link =
    node.author?.link && node.author?.link[0] && node.author?.link[0]?.link && node.author?.link[0]?.link[0]
      ? node.author?.link[0]?.link[0]
      : undefined;
  const { singleProps } = useLinkProps(link);
  const { internalHost } = useRequestHeaders();

  return (
    <JsonLd<Article>
      item={{
        // eslint-disable-next-line @if6/fi-no-absolute-urls
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: node.headline,
        description: node.description,
        image: {
          '@type': 'ImageObject',
          url: getAbsoluteUrlForMedia(node.media_1x1, internalHost),
        },
        datePublished: node.articlePublishedFirstAt,
        dateModified: node.articlePublishedAt,
        author: node.author
          ? {
              '@type': node.author.type,
              name: node.author.authorName,
              url: singleProps.href,
              image: {
                '@type': 'ImageObject',
                url: getAbsoluteUrlForMedia(node.author.media, internalHost),
              },
            }
          : undefined,
        publisher: node.publisher
          ? {
              '@type': 'Organization',
              name: node.publisher.name,
              url: node.publisher.url,
              logo: {
                '@type': 'ImageObject',
                url: getAbsoluteUrlForMedia(node.publisher.logo, internalHost),
              },
            }
          : undefined,
      }}
    />
  );
};

export class Consumer extends React.Component<Props, State> {
  state = {
    error: false,
    errorName: '',
    errorMessage: '',

    node: this.props.node,
  };

  static contextType = RequestHeadersContext;

  declare context: React.ContextType<typeof RequestHeadersContext>;

  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    // Array?
    if (
      Array.isArray(nextProps.node) ||
      Array.isArray(nextState.node) ||
      Array.isArray(this.props.node) ||
      Array.isArray(this.state.node)
    ) {
      return true;
    }

    // Node ID changed
    if (nextProps.node?.id !== this.props.node?.id || nextState.node?.id !== this.state.node?.id) {
      return true;
    }

    // An Error occurred
    if (nextState.error !== this.state.error) {
      return true;
    }

    // Eager changed
    if (nextState.node?.__eager?.state !== this.state.node?.__eager?.state) {
      return true;
    }

    return false;
  }

  componentDidMount(): void {
    const { node } = this.state;

    if (!node || Array.isArray(node)) {
      return;
    }

    if (node?.__eager?.state === 'partial') {
      const url = new URL(window.location.origin);
      url.pathname = '/api/eager';
      url.searchParams.set('id', `${node.id}`);
      url.searchParams.set(node['publication_state'], `${node.id}`);
      url.searchParams.set('model', node.__contentType);

      fetch(url.toString())
        .then((response) => response.json())
        .then((data) => {
          this.setState({ node: data.node });
        });
    }
  }

  protected renderArray(node: SingleNode[]): React.ReactNode {
    const Consumer = this.constructor as React.FC<Props>;
    return node.map((element) => (
      <Consumer key={`${element.__component || element.__contentType}.${element.id}`} node={element} />
    ));
  }

  protected renderLoadingState(node: SingleNode): React.ReactNode {
    if (isContentOpener(node)) {
      return <Skeleton animation="wave" variant="rectangular" width="100%" height={542} />;
    }

    if (isContentImagesInformative(node) || isContentImagesDecorative(node)) {
      return (
        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          sx={{ aspectRatio: `${node.media[0]?.width}/${node.media[0]?.height}`, height: 'auto' }}
        />
      );
    }

    return <Skeleton width={100} height={30} />;
  }

  private _internalHost;
  private getInternalHost() {
    return (this._internalHost ??= this.context.internalHost);
  }

  protected renderContentOpenerNode(node: ContentOpener, page: Page) {
    const Consumer = this.constructor as React.FC<Props>;
    const mediaFill = node.layout === 'serviceAndCampaignPage' ? 'none' : node.mediaFill;

    // OpenerVideo:Player: https://github.com/cookpete/react-player/issues/1474
    const thumbnailUrl = node.openerVideo?.thumbnail
      ? `${node.openerVideo.thumbnail['url']}`
      : node.openerVideo?.meta['thumbnailUrl'];
    return (
      <React.Fragment key={`${node.__component}-${node.id}`}>
        {node.layout === 'servicePage' || node.layout === 'serviceAndCampaignPage' ? (
          <Breadcrumbs node={page} key="breadcrumbs" />
        ) : null}
        <Opener node={node} key={node.id}>
          {node.openerVideo ? (
            <OpenerVideo
              Player={ReactPlayer}
              {...node.openerVideo}
              thumbnail={<Image src={thumbnailUrl} loading="eager" alt="" layout="fill" quality={10} />}
              mediaFill={mediaFill}
            />
          ) : node.media ? (
            <OpenerMedia mediaFill={mediaFill}>
              <Image
                src={node.media.url}
                height={node.media.height}
                width={node.media.width}
                alt={node.media.alternativeText || ''}
                loading="eager"
                priority={true}
              />
            </OpenerMedia>
          ) : null}
          <OpenerContent disableHorizontalPadding={node.layout === 'serviceAndCampaignPage'}>
            {node.overline?.text || node.headline?.text ? (
              <OpenerHead overline={node.overline?.text}>
                <Consumer
                  node={
                    {
                      __component: 'intern.headline-richtext',
                      id: node?.headline?.id,
                      text: node?.headline?.text,
                      innerPadding: 'horizontal',
                      variant: ['productPage', 'servicePage', 'serviceAndCampaignPage'].includes(node.layout)
                        ? 'openerFullPage'
                        : 'h1',
                      color: 'unset',
                      component: 'h1',
                    } as InternHeadlineRichtext
                  }
                />
              </OpenerHead>
            ) : null}
            {node.firstSubline || node.secondSubline || node.topInfoBox ? (
              <OpenerBody>
                {node.firstSubline?.text ? (
                  <Typography variant="body2" component="p" color="inherit" innerPadding="horizontal">
                    {node.firstSubline?.text}
                  </Typography>
                ) : null}
                {node.topInfoBox ? (
                  <PortalsTopInfoBox alternativeHeadline={node.topInfoBox.alternativeHeadline} size="small">
                    {node.topInfoBox.items.map((item) => {
                      return (
                        <TopInfoBoxItemText
                          key={item.id}
                          tooltip={
                            item.tooltip ? (
                              <Consumer
                                node={
                                  {
                                    __component: 'intern.text',
                                    text: item.tooltip,
                                    variant: 'body2',
                                  } as Node
                                }
                              />
                            ) : (
                              false
                            )
                          }
                        >
                          <Consumer
                            node={
                              {
                                __component: 'intern.text',
                                ...item,
                                variant: 'body2',
                                color: 'inherit',
                                headlineColor: 'inherit',
                                innerPadding: 'none',
                              } as Node
                            }
                          />
                        </TopInfoBoxItemText>
                      );
                    })}
                  </PortalsTopInfoBox>
                ) : null}
                {node.secondSubline?.text ? (
                  <Typography variant="body2" component="p" color="inherit" innerPadding="horizontal">
                    {node.secondSubline?.text}
                  </Typography>
                ) : null}
              </OpenerBody>
            ) : null}
            {node.content?.length ? (
              <OpenerFoot>
                <Consumer node={node.content} />
              </OpenerFoot>
            ) : null}
          </OpenerContent>
          {node.actions?.length || node.layout === 'productPage' ? (
            <OpenerActions>
              <Consumer node={node.actions} />
              {node.layout === 'productPage' ? <Breadcrumbs node={page} /> : null}
            </OpenerActions>
          ) : null}
        </Opener>
      </React.Fragment>
    );
  }

  protected renderContentNode(node: Array<unknown>): React.ReactNode[] {
    const Consumer = this.constructor as React.FC<Props>;

    if (!node?.length) {
      return null;
    }

    return node.map((item) => {
      if (
        isContentFAQ(item) ||
        isContentTrustElement(item) ||
        isInternText(item) ||
        isContentArticleText(item) ||
        isContentIntro(item) ||
        isContentInPageNavigation(item) ||
        isFormsSparkasseAssignmentForm(item) ||
        isFormsSparkasseAssignmentBox(item) ||
        isContentInfo(item) ||
        isContentAdviceBox(item) ||
        isContentNumberCounter(item)
      ) {
        return (
          <FinalContainer key={item.id} id={`${item.__component}.${item.id}`} disableGaping={true} maxWidth="text">
            <Consumer node={item} />
          </FinalContainer>
        );
      }
      if (isContentTopInfoBox(item) || isBenefitsList(item) || isContentNumberDivider(item) || isContentButton(item)) {
        return (
          <FinalContainer key={`${item.__component}.${item.id}`} id={`${item.__component}.${item.id}`} maxWidth="text">
            <Consumer node={item} />
          </FinalContainer>
        );
      }
      if (isInternInstituteSpecificContentItem(item) || isContentOpenerLandingPage(item)) {
        return <Consumer key={`${item.__component}.${item.id}`} node={item} />;
      }

      return (
        <FinalContainer key={`${item['__component']}.${item['id']}`} id={`${item['__component']}.${item['id']}`}>
          <Consumer node={item} />
        </FinalContainer>
      );
    });
  }

  protected isTextContainerComponentWithGap(item: SingleNode): item is SingleNode {
    return !!(
      isContentTopInfoBox(item) ||
      isBenefitsList(item) ||
      isContentNumberDivider(item) ||
      isContentButton(item) ||
      (isContentImagesInformative(item) && item.size === 'text') ||
      (isContentImagesDecorative(item) && item.size === 'text')
    );
  }

  protected isDirectConsumerComponent(item: SingleNode): item is SingleNode {
    return !!(
      isContentInstituteSpecificContent(item) ||
      isContentABTestingContent(item) ||
      isContentAdviceBox(item) ||
      isContentPersonalizedContent(item)
    );
  }

  protected isTextContainerComponentWithoutGap(item: SingleNode): item is SingleNode {
    return !!(
      isContentFAQ(item) ||
      isContentTrustElement(item) ||
      isInternText(item) ||
      isContentArticleText(item) ||
      isContentIntro(item) ||
      isContentInPageNavigation(item) ||
      isFormsSparkasseAssignmentForm(item) ||
      isFormsSparkasseAssignmentBox(item) ||
      isContentInfo(item) ||
      isContentNumberCounter(item) ||
      isContentSeaExitBox(item) ||
      isContentCooperationInfobox(item) ||
      isContentStepper(item) ||
      isContentPortraitBox(item) ||
      isContentDatawrapperCharts(item)
    );
  }

  protected isTextContainerComponentWithoutGutters(item: SingleNode): item is SingleNode {
    return !!isContentCTASection(item);
  }

  protected renderNode(node: SingleNode): React.ReactNode {
    const Consumer = this.constructor as React.FC<Props>;
    const renderNode = (node) => <Consumer node={node} />;

    if (isPage(node)) {
      const pathname = node.slugPath?.endsWith('/') ? node.slugPath : `${node.slugPath}.html`;
      const canonicalUrl = `${this.getInternalHost()}${pathname}`;
      const updateTime = node['updatedAt'];

      const openerWithOwnBreadcrumbs = node.content.find(
        (node) => isFullPageContentOpener(node) || isContentOpenerLandingPage(node)
      );

      return (
        <React.Fragment key={`page.${node.id}`}>
          <Head>
            <meta key="og:updated_time" property="og:updated_time" content={updateTime.toString()} />
            <meta key="og:url" property="og:url" content={canonicalUrl} />
          </Head>
          <SiteSearchMeta key={`siteSearchMeta.${node.id}`} node={node} />
          {!node.meta?.noindex && <Canonical key="canonical" node={node.canonical} fallbackUrl={canonicalUrl} />}
          <Consumer node={node.meta} key={`meta.${node.id}`} />
          <Consumer node={node.socialMeta} key="socialMeta" />
          <Consumer node={node.articleMeta} key="articleMeta" />
          <Consumer node={node.header} key="header" />
          {openerWithOwnBreadcrumbs ? null : <Breadcrumbs node={node} key="breadcrumbs" />}
          <Box component="main" tabIndex={-1} aria-label="inhalt" id={`${SKIP_TO_MAIN_CONTENT_ANCHOR}`}>
            {node.content.map((item) => {
              // TODO: can we rework this function?

              if (isContentOpener(item)) {
                return this.renderContentOpenerNode(item, node);
              }

              if (isContentOpenerLandingPage(item)) {
                return (
                  <OpenerLandingPage
                    key={`${item.__component}.${item.id}`}
                    node={item}
                    page={node}
                    renderNode={renderNode}
                  />
                );
              }
              if (this.isDirectConsumerComponent(item)) {
                return <Consumer key={`${item.__component}.${item.id}`} node={item} />;
              }
              // Small Grid: Switch Case
              if (this.isTextContainerComponentWithoutGap(item)) {
                return (
                  <FinalContainer
                    component={isContentArticleText(item) ? 'section' : undefined}
                    key={`${item.__component}.${item.id}`}
                    id={`${item.__component}.${item.id}`}
                    disableGaping={true}
                    maxWidth="text"
                  >
                    <Consumer node={item} />
                  </FinalContainer>
                );
              }

              if (this.isTextContainerComponentWithGap(item)) {
                return (
                  <FinalContainer
                    key={`${item.__component}.${item.id}`}
                    id={`${item.__component}.${item.id}`}
                    maxWidth="text"
                  >
                    <Consumer node={item} />
                  </FinalContainer>
                );
              }

              if (this.isTextContainerComponentWithoutGutters(item)) {
                return (
                  <FinalContainer
                    key={`${item.__component}.${item.id}`}
                    id={`${item.__component}.${item.id}`}
                    disableGutters={true}
                  >
                    <Consumer node={item} />
                  </FinalContainer>
                );
              }

              return (
                <FinalContainer
                  key={`${item['__component']}.${item['id']}`}
                  id={`${item['__component']}.${item['id']}`}
                >
                  <Consumer node={item} />
                </FinalContainer>
              );
            })}
          </Box>
          <Consumer node={node.flex_footer} key="flex_footer" />
          <Consumer node={node.footer} key="footer" />
          <Consumer node={node.quick_navi} key="quick_navi" />
          <Consumer node={node.legal_footer} key="legal_footer" />
          {node.pageViewUserContext ? <Consumer node={node.pageViewUserContext} /> : null}
          <ServiceBoxDialog renderNode={renderNode} key="service_box" />
          <SparkasseAssignmentFormModal />
        </React.Fragment>
      );
    }

    if (isGenericComparison(node)) {
      return <GenericComparison node={node} />;
    }

    if (isContentOpenerLandingPage(node)) {
      return <OpenerLandingPage node={node} renderNode={renderNode} />;
    }

    if (isGenericComparisonEntry(node)) {
      // TODO: this is an hack, provide a better way with index
      return <GenericComparisonEntry node={node} index={+this.props.node['index'] + 1} />;
    }

    if (isInternGenericComparisonPriceFree(node)) {
      return <GenericComparisonPriceFree text={node.text} />;
    }

    if (isInternGenericComparisonPriced(node)) {
      return (
        <GenericComparisonPricePriced
          headline={node.headline}
          highlighted={node.highlighted}
          price={node.price}
          period={node.period}
        />
      );
    }

    if (isContentMenuLinkList(node)) {
      return (
        <MenuLinkList headline={node.headline}>
          {node.items.map((link) => (
            <MenuLink key={link.id} node={link} renderNode={renderNode} />
          ))}
        </MenuLinkList>
      );
    }

    if (isQuickNavi(node)) {
      return <QuickNavi node={node} />;
    }

    if (isHeader(node)) {
      return (
        <Header id={`${node.__contentType}.${node.id}`} createTheme={createHeaderTheme}>
          <Consumer node={node.content} />
        </Header>
      );
    }

    if (isFooter(node)) {
      if (!node.content || node.content.length === 0) {
        return null;
      }

      return (
        <Footer id={`${node.__contentType}.${node.id}`}>
          {node.content.map((item) => {
            return (
              <FinalContainer key={item.id} id={`${item.__component}.${item.id}.xyz`}>
                <Consumer node={item} />
              </FinalContainer>
            );
          })}
        </Footer>
      );
    }

    if (isContentGenericComparison(node)) {
      return <GenericComparison node={node.reference} />;
    }

    if (isFlexFooter(node)) {
      if (!node.content || node.content.length === 0) {
        return null;
      }

      return (
        <FlexFooter id={`${node.__contentType}.${node.id}`}>
          {node.content.map((item) => (
            <FinalContainer key={item.id} id={`${item.__component}.${item.id}`}>
              <Consumer node={item} />
            </FinalContainer>
          ))}
        </FlexFooter>
      );
    }

    if (isContentSeaExitBox(node)) {
      return <SeaExitBox node={node} renderNode={renderNode} />;
    }

    if (isHeaderNavigation(node) && isNavigation(node.navigation)) {
      return (
        <ThemeProvider theme={theme}>
          <Navigation node={node} createHeaderTheme={createHeaderTheme} renderNode={renderNode} />
        </ThemeProvider>
      );
    }

    if (isInternHeadline(node)) {
      return <Typography component={node.component}>{node.text}</Typography>;
    }

    if (isContentIntro(node)) {
      const text = node['summary'] || node['intro'];

      return (
        <ContentIntro node={node}>
          <Consumer node={{ __component: 'intern.text', id: `text.${node.id}`, text } as Node} />
        </ContentIntro>
      );
    }

    if (isContentAudio(node)) {
      return <Audio node={node} />;
    }

    if (isContentAccordionDownload(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <AccordionDownload node={node} />
        </>
      );
    }

    if (isServiceTeaserGallery(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <ServiceTeaserGallery {...node} />
        </>
      );
    }

    if (isWallTeaserGallery(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <WallTeaserGallery {...node} />
          <DividerLink dividerLink={node.dividerLink} />
        </>
      );
    }

    if (isInternMeta(node)) {
      const robots = [node.noindex ? 'noindex' : 'index', node.nofollow ? 'nofollow' : 'follow'].join(', ');

      return (
        <Head>
          <title key="html:title">{node.title}</title>
          <meta key="description" name="description" content={node.description} />
          <meta key="robots" name="robots" content={robots} />
          {node.ld && (
            <script
              key="ld"
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(node.ld, null, 2) }}
            />
          )}
        </Head>
      );
    }

    if (isInternArticleMeta(node)) {
      return <RenderJsonLD node={node} />;
    }

    if (isInternSocialMeta(node)) {
      const { media } = node;
      const pathname = media ? `/api/download/${media.id}/${media.name}?hash=${media.hash}` : null;
      const mediaUrl = media ? `${this.getInternalHost()}${pathname}` : null;
      return (
        <Head>
          {node.title && <meta key="og:title" property="og:title" content={node.title} />}
          {node.description ? <meta key="og:description" property="og:description" content={node.description} /> : null}
          {mediaUrl ? <meta key="og:image" property="og:image" content={mediaUrl} /> : null}
          <meta key="og:locale" property="og:locale" content="de_DE" />
          <meta key="og:site_name" property="og:site_name" content="Sparkasse_de" />
          <meta key="og:type" property="og:type" content={node.type} />
        </Head>
      );
    }

    if (isContentIconLinkList(node)) {
      return (
        <IconList id={`${node.__component}.${node.id}`} spacing={{ xs: 3, lg: 6 }}>
          {node.items.map((item) => {
            return <IconButton key={item.id} node={item} />;
          })}
        </IconList>
      );
    }

    if (isHeaderBar(node)) {
      return <HeaderBar node={node} renderNode={renderNode} />;
    }

    if (isContentFooterBar(node)) {
      return (
        <Grid container justifyContent="space-between" alignItems="center" id={`${node.__component}.${node.id}`}>
          <Grid>
            <Consumer node={node.linkList} />
          </Grid>
          <Grid>
            <Consumer node={node.button} />
          </Grid>
        </Grid>
      );
    }

    if (isLegalFooter(node)) {
      return <LegalFooter node={node} createTheme={createFooterTheme} />;
    }

    if (isContentButton(node)) {
      return <Button node={node} />;
    }

    if (isHeaderButtonList(node)) {
      // TODO: The UL/LI here are only for SEO, see the discussion in SPKDE-11730
      // TODO: remove as soon as we want to optimize DOM nodes.
      return (
        <HeaderButtonList node={node}>
          {node.items.map((element) => (
            <li key={`${element.__component}${element.id}`}>
              <Consumer node={element} />
            </li>
          ))}
        </HeaderButtonList>
      );
    }

    if (isContentServiceTeaserGallery(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentWallTeaserGallery(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentHighCharts(node)) {
      if (node.highChartJSON) {
        return <HighCharts highChartJSON={node.highChartJSON} id={node.id} />;
      }
      return null;
    }

    if (isContentServiceLinkList(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentAppCategoryLinkList(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentCategoryLinkList(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentPortraitBox(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentAdviceBox(node)) {
      return (
        <FinalContainer id={`${node.__component}.${node.id}`} disableGaping={true} maxWidth="text">
          <Consumer node={node.reference} />
        </FinalContainer>
      );
    }
    if (isContentAppButton(node)) {
      return (
        <FinalContainer id={`${node.__component}.${node.id}`} disableGaping={true} maxWidth="text">
          <Consumer node={node.reference} />
        </FinalContainer>
      );
    }

    if (isContentCooperationInfobox(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentCiveySurvey(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <CiveySurvey surveyId={node.surveyId} />
        </>
      );
    }

    if (isContentCTASection(node)) {
      return <CTASection node={node} />;
    }

    if (isContentTrustElement(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentInPageNavigation(node)) {
      return (
        <InPageNavigationPage
          title={node.title}
          links={node.links?.map((link) => ({
            text: link.text,
            anchor: link.anchor,
          }))}
          iconList={
            <IconList direction="row" id={`${node.__component}.${node.id}`} spacing={{ xs: 4, lg: 6 }}>
              {node.iconList.map((item) => {
                return <IconButton circular key={item.id} node={item} />;
              })}
            </IconList>
          }
        />
      );
    }

    if (isContentStepper(node)) {
      return (
        <>
          {node.intro && (
            <>
              <Consumer node={node.intro} />
              <Box mb={{ xs: 5, lg: 8 }} />
              <Divider />
              <Box mb={{ xs: 8, lg: 12 }} />
            </>
          )}
          <Stepper
            steps={node.steps.map((step) => ({
              number: step.number,
              image: step.media?.url,
              title: step.title,
              text: (
                <Consumer node={{ __component: 'intern.text', text: step.text, innerPadding: 'none' } as InternText} />
              ),
            }))}
          />
        </>
      );
    }

    if (isContentInPageNavigationOpener(node)) {
      return <InPageNavigationOpener node={node} />;
    }

    if (isAppCategoryLinkList(node)) {
      return (
        <>
          <Consumer node={node.intro} />
          <AppCategoryLinkList node={node} />
        </>
      );
    }

    if (isCategoryLinkList(node)) {
      return (
        <>
          <Consumer node={node.intro} />
          <CategoryLinkList node={node} />
        </>
      );
    }

    if (isContentAppFeaturePreview(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isAppFeaturePreview(node)) {
      return <AppFeaturePreview node={node} renderNode={renderNode} />;
    }

    if (isServiceLinkList(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}

          <ServiceLinkList node={node} />
        </>
      );
    }

    if (isContentTopInfoBox(node)) {
      //TODO: Divider sx besser lösen

      return (
        <>
          <Divider sx={{ marginBottom: { xs: 7, md: 7, lg: 8 } }} />
          <TopInfoBox node={node} />
          <Divider sx={{ marginTop: { xs: 7, md: 7, lg: 8 } }} />
        </>
      );
    }

    if (isTrustElement(node)) {
      return (
        <Stack display="flex" gap={{ xs: 8, lg: 10 }}>
          {node.items?.map((item) => (
            <TrustElement
              key={item.id}
              headline={item.headline}
              text={item.text}
              image={
                item.media && (
                  <Image
                    src={item.media.url}
                    height={item.media.height}
                    width={item.media.width}
                    alt={item.media.alternativeText}
                    title={item.media.caption || null}
                    style={{
                      objectFit: 'contain',
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      maxHeight: '160px',
                    }}
                  />
                )
              }
            />
          ))}
          <Consumer node={node.cta} />
        </Stack>
      );
    }

    if (isContentTrustImages(node)) {
      return <TrustImages node={node} />;
    }

    if (isContentLinda(node)) {
      return <Linda node={node} />;
    }

    if (isBenefitsList(node)) {
      return (
        <>
          <Consumer node={node.intro} />
          <BenefitsList node={node} />
        </>
      );
    }

    if (isContentDatawrapperCharts(node)) {
      return <DatawrapperCharts node={node} />;
    }

    if (isBenefitsListImage(node)) {
      return <BenefitsListImage node={node} />;
    }

    if (isContentSimpleTable(node)) {
      return (
        <>
          <Consumer node={node.intro} />
          <SimpleTable node={node} />
        </>
      );
    }

    if (isInternHeadlineRichtext(node)) {
      const text = node['text'];
      const textVariant = node['variant'];
      const textColor = node['color'];
      const innerPadding = node['innerPadding'];
      const component = node['component'];

      return node?.text?.includes('"blocks":') ? (
        <RichTextRenderer
          text={text}
          textColor={textColor}
          textVariant={textVariant}
          innerPadding={innerPadding}
          component={component}
        />
      ) : (
        <Typography variant={textVariant} color={textColor} innerPadding={innerPadding} component={component}>
          {text}
        </Typography>
      );
    }

    if (isInternText(node) || isContentArticleText(node)) {
      const text = node['text'];
      const textVariant = node['variant'];
      const textColor = node['color'];
      const headlineColor = node['headlineColor'];
      const innerPadding = node['innerPadding'];

      const ParagraphConsumer = ({ node }) => (
        <TextConsumer node={node} options={{ textVariant, textColor, innerPadding }} />
      );
      const ListConsumer = ({ node }) => <TextConsumer node={node} options={{ text, textColor, innerPadding }} />;
      const HeadlineConsumer = ({ node }) => (
        <TextConsumer node={node} options={{ text, headlineColor, innerPadding }} />
      );

      const components = {
        h1: HeadlineConsumer,
        h2: HeadlineConsumer,
        h3: HeadlineConsumer,
        h4: HeadlineConsumer,
        h5: HeadlineConsumer,
        h6: HeadlineConsumer,
        em: TextConsumer,
        strong: TextConsumer,
        p: ParagraphConsumer,
        ul: ListConsumer,
        ol: ListConsumer,
        li: ListConsumer,
        a: TextConsumer,
        blockquote: TextConsumer,
        code: TextConsumer,
        hr: TextConsumer,
        img: TextConsumer,
        pre: TextConsumer,
        del: TextConsumer,
        br: TextConsumer,
      };
      return node.text ? (
        <RichTextRenderer
          text={text}
          textColor={textColor}
          components={components}
          renderLink={(link, children) => <Link link={link}>{children}</Link>}
          renderImage={(src, alt) => (
            <Image src={src} alt={alt} width="300" height="300" style={{ display: 'block', marginBottom: '5px' }} />
          )}
          textVariant={textVariant}
          innerPadding={innerPadding}
        />
      ) : null;
    }

    if (isInternIcon(node)) {
      const Icon = loadIcon(node.icon);

      if (!Icon) {
        return null;
      }

      return <Icon fontSize="inherit" />;
    }

    if (isContentProductLinkList(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isProductLinkList(node)) {
      return (
        <>
          <Consumer node={node.intro} />
          <ProductLinkList node={node} />
        </>
      );
    }

    if (isContentOpenerContentPage(node)) {
      return (
        <OpenerContentPage layout={node.layout}>
          <OpenerContentPageMedia>
            {node.media ? (
              <Image
                src={node.media.url}
                height={node.media.height}
                width={node.media.width}
                alt={node.media.alternativeText || ''}
                style={{
                  objectFit: 'fill',
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  lineHeight: 1.5,
                  fontSize: '16px',
                  fontFamily: 'monospace',
                  backgroundColor: '#292929',
                  color: 'white',
                }}
                loading="eager"
              />
            ) : null}
          </OpenerContentPageMedia>
          <OpenerContentPageContent>
            {node.headline?.text ? (
              <Box order={2}>
                <Consumer
                  node={
                    {
                      __component: 'intern.headline-richtext',
                      id: node.headline.id,
                      text: node.headline.text,
                      variant: 'c1',
                      component: 'h1',
                      innerPadding: 'none',
                    } as InternHeadlineRichtext
                  }
                />
              </Box>
            ) : null}
            {node.overline?.text ? (
              <Typography variant="h5" color="text.overline" component="div" order={1}>
                {node.overline?.text}
              </Typography>
            ) : null}
            {node.text ? (
              <Typography variant="h4" component="div" order={3}>
                {node.text}
              </Typography>
            ) : null}
          </OpenerContentPageContent>
          <OpenerContentPageActions>
            <Consumer node={node.actions} />
          </OpenerContentPageActions>
        </OpenerContentPage>
      );
    }

    if (
      isContentCard(node) ||
      isCardContentCard(node) ||
      isMetaCard(node) ||
      isCardMetaCard(node) ||
      isInfoCard(node) ||
      isCardInfoCard(node) ||
      isProductCard(node) ||
      isCardProductCard(node) ||
      isServiceCard(node) ||
      isCardServiceCard(node)
    ) {
      return <CardConsumer node={node} />;
    }

    if (isPortraitBox(node)) {
      if (node.avatar && node.avatar.image) {
        node.avatar.image.alternativeText = '';
      }
      return (
        <>
          {node.intro ? (
            <>
              <Consumer node={node.intro} />
              <Spacer />
            </>
          ) : null}

          <PortraitBox node={node} renderNode={renderNode} />
        </>
      );
    }

    if (isAdviceBox(node)) {
      return <AdviceBox node={node} renderNode={renderNode} createTheme={createLightTheme} />;
    }

    if (isContentIfClickPath(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isIfClickPath(node)) {
      return (
        <>
          {node.intro ? (
            <Box marginLeft="auto" marginRight="auto" maxWidth="text">
              <Consumer node={node.intro} />
            </Box>
          ) : null}
          <IfClickPath {...node} />
          <Box marginLeft="auto" marginRight="auto" maxWidth="text">
            {node.tour ? (
              <Info node={{ __component: 'content.info', id: node.id }}>
                <>
                  <Typography variant="body2" fontWeight="bold">
                    Hinweis:
                  </Typography>
                  <Typography variant="body2">
                    Die Vorgehensweise ist beispielhaft dargestellt und kann je nach mobilem Endgerät oder Institut
                    geringfügig abweichen.
                  </Typography>
                </>
              </Info>
            ) : null}
          </Box>
        </>
      );
    }

    if (isCooperationInfobox(node)) {
      return <CooperationInfobox node={node} renderNode={renderNode} />;
    }

    if (isContentFAQ(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentVideo(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <Video {...node} />
        </>
      );
    }

    if (isFAQ(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Spacer /> : null}
          <FAQ {...node} />
        </>
      );
    }

    if (isContentImagesDecorative(node)) {
      return node.media?.length > 0 ? (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          <ImagesDecorative {...node} />
        </>
      ) : null;
    }

    if (isContentImagesInformative(node)) {
      return node.media?.length > 0 ? (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          <ImagesInformative {...node} />
        </>
      ) : null;
    }

    if (isContentImageWall(node)) {
      return (
        <>
          {node.intro && (
            <FinalGridContainer>
              <FinalGridItem lg={8} md={12}>
                <Consumer node={node.intro} />
              </FinalGridItem>
            </FinalGridContainer>
          )}
          <ImageWall {...node} />
        </>
      );
    }

    if (isFormsContactForm(node)) {
      return <ContactForm node={node} />;
    }

    if (isFormsModernisationCalculator(node)) {
      return <ModernisationCalculator node={node} />;
    }

    if (isSplitPaper(node)) {
      return <SplitPaper node={node} renderNode={renderNode} />;
    }

    if (isFormsBudgetCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <BudgetCalculator node={node} renderNode={renderNode} />
        </>
      );
    }

    if (isFormsRepaymentCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <RepaymentCalculator node={node} renderNode={renderNode} />
        </>
      );
    }

    if (isFormsPrivateLoanCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <PrivateLoanCalculator node={node} />
        </>
      );
    }

    if (isFormsSparkasseAssignmentBox(node)) {
      return (
        <SparkasseAssignmentBox
          node={node}
          renderNode={renderNode}
          createLightTheme={createLightTheme}
          createDarkTheme={createDarkTheme}
        />
      );
    }

    if (isFormsSparkasseAssignmentForm(node)) {
      return <SparkasseAssignmentForm node={node} />;
    }

    if (isFormsRentalCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <RentalCalculator node={node} />
        </>
      );
    }

    if (isSaveplanCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <SaveplanCalculator node={node} />
        </>
      );
    }

    if (isIbanCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <IbanCalculator node={node} />
        </>
      );
    }

    if (isFormsPensionGapCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <PensionGapCalculator node={node} />
        </>
      );
    }

    if (isFormsHomeConstructionLoanCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <HomeConstructionLoanCalculator node={node} />
        </>
      );
    }

    if (isBruttoNettoCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <BruttoNettoCalculator node={node} />
        </>
      );
    }

    if (isHouseholdCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <HouseholdCalculator node={node} />
        </>
      );
    }

    if (isIbanCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <IbanCalculator node={node} />
        </>
      );
    }

    if (isFormsCapitalCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <CapitalCalculator node={node} />
        </>
      );
    }

    if (isAdditionalCostCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <AdditionalCostCalculator node={node} />
        </>
      );
    }

    if (isFormsFuelCostCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <FuelCostCalculator node={node} />
        </>
      );
    }

    if (isFormsPensionCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <PensionCalculator node={node} />
        </>
      );
    }

    if (isFormsSkpCalculator(node)) {
      return <SkpCalculator node={node} />;
    }

    if (isFormsPensionGapCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <PensionGapCalculator node={node} />
        </>
      );
    }

    if (isFormsPriceEstimator(node)) {
      return <PriceEstimator {...node} />;
    }

    if (isDiscountCalculator(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <DiscountCalculator node={node} />
        </>
      );
    }

    if (isContentQuote(node)) {
      return <Quote {...node} />;
    }

    if (isContentNumberDivider(node)) {
      return (
        <Divider variant="listicle" id={`divider-${node.displayNumber}`}>
          {node.displayNumber}
        </Divider>
      );
    }

    if (isInternServiceLink(node)) {
      return <ServiceLink key={node.id} node={node} />;
    }

    if (isPageLinkList(node)) {
      return <PageLinkList linkedPages={node?.reference?.linked_pages} />;
    }

    if (isLinkIndexList(node)) {
      return <PageLinkList linkedPages={node?.linked_pages} />;
    }

    if (isContentAvatar(node)) {
      return (
        <Avatar
          id={node.text}
          node={node}
          actions={node.actions.map((action, index) => {
            return (
              <>
                <Box
                  key={`avatar${node.id}-action${action.id}`}
                  mr={index === node.actions.length - 1 ? 0 : { lg: 8, xs: 1 }}
                  sx={{ display: node.layout === 'contact' ? { lg: 'none' } : 'block' }}
                >
                  <IconButton key={`avatar${node.id}-action${action.id}`} node={action} />
                </Box>
                {node.layout === 'contact' ? (
                  <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Consumer
                      node={
                        {
                          __component: 'content.button',
                          id: action.id,
                          link: action.link,
                          variant: 'inverted',
                          startIcon: action.icon,
                        } as ContentButton
                      }
                    />
                  </Box>
                ) : null}
              </>
            );
          })}
        />
      );
    }

    if (isContentInfo(node)) {
      return (
        <Info node={node}>
          <Consumer node={{ __component: 'intern.text', variant: 'body2', text: node['infoText'] } as Node} />
        </Info>
      );
    }

    if (isContentNumberCounter(node)) {
      return (
        <NumberCounter
          media={
            node.media ? (
              <Image
                alt=""
                src={node.media.url}
                width={node.media.width}
                height={node.media.height}
                style={{ maxWidth: '100%', height: '100%', display: 'block' }}
              />
            ) : null
          }
          headline={
            node.headlineTitle ? (
              <Typography component="div" variant="h3">
                {node.headlineTitle}
              </Typography>
            ) : null
          }
          text={
            node.text ? (
              <Typography component="p" variant="body1">
                {node.text}
              </Typography>
            ) : null
          }
          duration={node.duration}
          endValue={node.endValue}
          startValue={node.startValue}
          suffix={node.unit}
        />
      );
    }

    if (isProductComparison(node)) {
      return (
        <>
          {node.intro && <Consumer node={node.intro} />}
          <ProductComparison schema={node.schema} />
        </>
      );
    }

    if (isContentComparisonTable(node)) {
      return (
        <>
          {node.intro && <Consumer node={node.intro} />}
          <ComparisonTable node={node} renderNode={renderNode} />
        </>
      );
    }

    if (isContentSiteSearch(node)) {
      return (
        <>
          {node.intro && <Consumer node={node.intro} />}
          <FifiConfigurationContext.Provider value={fifiConfig}>
            <SiteSearchContextProvider
              initialSiteSearchCategories={node.categories && node.categories.length > 0 ? node.categories : null}
            >
              <SiteSearch node={node} />
            </SiteSearchContextProvider>
          </FifiConfigurationContext.Provider>
        </>
      );
    }

    if (isContentSiteSearchSuggest(node)) {
      return (
        <>
          {node.intro && <Consumer node={node.intro} />}
          <FifiConfigurationContext.Provider value={fifiConfig}>
            <SiteSearchSuggest node={node} />
          </FifiConfigurationContext.Provider>
        </>
      );
    }

    if (isContentFinder(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isFinder(node)) {
      return <Finder node={node} renderNode={renderNode} />;
    }

    if (isContentQuiz(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isQuiz(node)) {
      return <Quiz node={node} renderNode={renderNode} />;
    }

    if (isAuthor(node)) {
      if (node.media) {
        node.media.alternativeText = '';
      }
      const headline = {
        __component: 'intern.headline',
        id: node.id,
        text: node.headline,
        component: 'div',
      } as InternHeadline;

      return (
        <>
          <Divider />
          <Avatar
            id={node.name}
            node={{
              __component: 'content.avatar',
              id: node.id,
              headline: [headline],
              text: node.authorName,
              subline: node.title,
              image: node.media,
              layout: 'default',
              actions: null,
            }}
            actions={node.link.map((action, index) => {
              const button = {
                id: action.id,
                __component: 'content.button',
                variant: 'standard',
                color: 'inherit',
                size: 'small',
                fullWidth: false,
                startIcon: action.icon,
                link: action.link,
              } as ContentButton;

              const text = action.link ? action.link[0]?.text : '';

              return (
                <Box
                  key={`avatar${node.id}-action${action.id}`}
                  mr={index === node.link.length - 1 ? 0 : { lg: 8, xs: 1 }}
                  sx={{ display: 'block' }}
                >
                  <Button node={button}>{text}</Button>
                </Box>
              );
            })}
          />
          <Divider />
        </>
      );
    }

    if (isContentAuthor(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isContentJobSearch(node)) {
      return (
        <JobsConfigurationContext.Provider
          value={{
            ...JOBS_APP_CONFIG,
            jobMarketLogoPath: process.env.INSTITUTE_LOGO_PATH,
            jobMarketBasePath: process.env.NEXT_PUBLIC_JOB_MARKET_BASE_PATH,
            jobMarketDetailsRoute: process.env.NEXT_PUBLIC_JOB_MARKET_DETAILS_ROUTE,
            useHashRouter: 'false',
            friendlyCaptchaSitekey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITEKEY,
            friendlyCaptchaEndpointUrl: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_ENDPOINT_URL,
            yellowmapAutocompleteUrl: fifiConfig.yellowmapAutocompleteUrl,
            yellowmapSystemPartner: fifiConfig.yellowmapSystemPartner,
            yellowmapApiKey: fifiConfig.yellowmapApiKey,
            yellowmapFrontendKey: fifiConfig.yellowmapFrontendKey,
            yellowmapApiUrl: fifiConfig.yellowmapApiUrl,
            showCompanyFilter: node.showCompanyFilter,
            showLogo: true,
          }}
        >
          {node.intro ? <Consumer node={node.intro} /> : null}
          {node.intro ? <Divider /> : null}
          <JobSearch node={node} />
        </JobsConfigurationContext.Provider>
      );
    }

    if (isContentPersonalizedContent(node)) {
      // TODO: Should this be `node.reference`?
      return this.renderContentNode(node.reference.content);
    }

    if (isContentABTestingContent(node)) {
      // TODO: Should this be `node.reference`?
      return this.renderContentNode(node.reference.content);
    }

    if (isContentInstituteSpecificContent(node)) {
      return this.renderContentNode([node.reference]);
    }

    if (isContentSCert(node)) {
      return (
        <>
          {node.intro ? <Consumer node={node.intro} /> : null}
          <SCert {...node} />
        </>
      );
    }

    if (isInternContentItem(node)) {
      return this.renderContentNode(node.content);
    }

    if (isInternInstituteSpecificContentItem(node)) {
      return (
        <InstituteSpecificContent
          node={node}
          renderNode={(node: Array<unknown>) => this.renderContentNode(node) as React.ReactNode}
        />
      );
    }

    if (isInternUserContext(node)) {
      return <Consumer node={node.contexts} />;
    }

    if (isUserContext(node)) {
      return <UserContext node={node} />;
    }

    if (isGirokontoConditions(node)) {
      return <GirokontoConditions node={node} />;
    }

    if (isGirokontoServices(node)) {
      return <GirokontoServices node={node} />;
    }

    if (isGirokontoBenefits(node)) {
      return <GirokontoBenefits node={node} />;
    }

    if (isGirokontoComparison(node)) {
      return <GirokontoComparison node={node} />;
    }
    if (isAppButton(node)) {
      return (
        <>
          <Consumer node={node.intro} />
          <AppButton node={node} />
        </>
      );
    }

    if (isContentAppReview(node)) {
      return <Consumer node={node.reference} />;
    }

    if (isAppReview(node)) {
      return (
        <>
          {node.intro && <Consumer node={node.intro} />}
          <AppReview {...node} />
        </>
      );
    }

    return (
      <Preview liveContent={null}>
        <pre>
          {typeof node}
          <hr />
          {JSON.stringify(node, null, 2)}
        </pre>
      </Preview>
    );
  }

  static getDerivedStateFromError(error: Error): State {
    return { error: true, errorMessage: error.message, errorName: error.name, node: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error({ error, errorInfo });
  }

  public render(): React.ReactNode {
    const { node, error, errorMessage, errorName } = this.state;

    if (error) {
      // This must be the same as in the catch clause downwards!
      return (
        <Preview liveContent={null}>
          <ErrorNotice headline={errorName} message={errorMessage} />
        </Preview>
      );
    }

    try {
      if (!node) {
        return null;
      }

      if (Array.isArray(node)) {
        return this.renderArray(node);
      }

      const stringNode = JSON.stringify(node);
      if (stringNode === '{"data":null}' || stringNode === '{}') {
        return null;
      }

      if (node?.__eager?.state === 'partial') {
        return this.renderLoadingState(node);
      }
      return this.renderNode(node);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        throw e;
      } else {
        console.error(e);
      }

      // This must be the same as in the catch clause upwards!
      return (
        <Preview liveContent={null}>
          <ErrorNotice headline={e.name} message={e.message} />;
        </Preview>
      );
    }
  }
}
